import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const ParentAuthContext = createContext(null);
export const useParentAuth = () => useContext(ParentAuthContext);

const STORAGE_TOKEN = "elan_parent_token";
const STORAGE_EMAIL = "elan_parent_email";
const STORAGE_NAME = "elan_parent_name";
const STORAGE_FAMILY = "elan_parent_family_id";

const readStoredUser = () => {
  const token = localStorage.getItem(STORAGE_TOKEN);
  const email = localStorage.getItem(STORAGE_EMAIL);
  if (!token || !email) return null;
  return {
    email,
    parentName: localStorage.getItem(STORAGE_NAME) || "",
    familyId: localStorage.getItem(STORAGE_FAMILY) || "",
  };
};

const persistSession = ({ token, email, parentName, familyId }) => {
  if (token) localStorage.setItem(STORAGE_TOKEN, token);
  if (email) localStorage.setItem(STORAGE_EMAIL, email);
  if (parentName !== undefined) localStorage.setItem(STORAGE_NAME, parentName || "");
  if (familyId !== undefined) localStorage.setItem(STORAGE_FAMILY, familyId || "");
};

const clearSession = () => {
  localStorage.removeItem(STORAGE_TOKEN);
  localStorage.removeItem(STORAGE_EMAIL);
  localStorage.removeItem(STORAGE_NAME);
  localStorage.removeItem(STORAGE_FAMILY);
};

export const ParentAuthProvider = ({ children }) => {
  // Restore immediately from localStorage so the UI is not blank on refresh
  const [user, setUser] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(() => !!localStorage.getItem(STORAGE_TOKEN));

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_TOKEN);
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/parent/auth/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        // Sliding refresh: backend returns a fresh 30-day token on every /me
        persistSession({
          token: data.access_token || token,
          email: data.email,
          parentName: data.parentName || "",
          familyId: data.family_id || "",
        });
        setUser({
          email: data.email,
          parentName: data.parentName || "",
          familyId: data.family_id || "",
        });
      } catch (err) {
        // Only wipe session on real 401 (invalid/expired token).
        // Network errors / 5xx: keep the stored session so the user is not kicked out.
        if (err?.response?.status === 401) {
          clearSession();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/parent/auth/login", { email, password });
    persistSession({
      token: data.access_token,
      email: data.email,
      parentName: data.parentName || "",
      familyId: data.family_id || "",
    });
    setUser({
      email: data.email,
      parentName: data.parentName || "",
      familyId: data.family_id || "",
    });
    return data;
  };

  const logout = () => {
    clearSession();
    setUser(null);
  };

  return (
    <ParentAuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </ParentAuthContext.Provider>
  );
};
